import React from 'react';
import PropTypes from 'prop-types';
import { isGlobalProgressStatus } from '../../../constants/status';
import GlobalProgress from '../../GlobalProgress';
import Router from '../Router/Router';

/**
 * Componente que decide entre mostrar GlobalProgress ou Router/children
 * @param {Object} props - Props do componente
 * @param {Object|null} props.displayStatus - Status atualmente exibido
 * @param {boolean} props.globalProgressBusy - Se o GlobalProgress está ocupado
 * @param {Object|null} props.statusForProgress - Status a ser passado para o GlobalProgress
 * @param {Function} props.onBusyChange - Callback quando GlobalProgress muda estado busy
 * @param {Array<Object>} [props.routes] - Array de rotas (opcional se usar children)
 * @param {React.ReactNode} [props.children] - Conteúdo a ser renderizado (opcional se usar routes)
 * @param {Object} props.displayLocation - Location atualmente exibido
 * @param {string} props.transitionStage - Estágio da transição de rota
 * @param {boolean} props.contentFadeOut - Se o conteúdo deve fazer fadeOut
 * @param {boolean} props.contentDelay - Se o conteúdo deve ter delay
 * @param {boolean} props.fromLoading - Se está vindo de loading
 * @returns {JSX.Element}
 */
const ContentSwitcher = ({
  displayStatus,
  globalProgressBusy,
  statusForProgress,
  onBusyChange,
  onTextVisible,
  routes,
  children,
  displayLocation,
  transitionStage,
  contentFadeOut,
  contentDelay,
  fromLoading,
}) => {
  // Renderiza GlobalProgress se há statusForProgress (captura status) ou está busy OU se displayStatus tem status
  // EXCETO se o status for SDK, fullscreen ou closing (não devem renderizar GlobalProgress)
  const hasStatusForProgress = isGlobalProgressStatus(statusForProgress);
  const hasDisplayStatus = isGlobalProgressStatus(displayStatus);
  const shouldRenderGlobalProgress = hasStatusForProgress || globalProgressBusy || hasDisplayStatus;

  // Mostra GlobalProgress (visível) se há displayStatus ou está busy
  const shouldShowStatus = hasDisplayStatus || globalProgressBusy;

  // Durante fade-out ou quando está visível: renderiza conteúdo + GlobalProgress
  if (shouldRenderGlobalProgress) {
    const showContent = !shouldShowStatus || contentFadeOut;

    if (children) {
      const classNames = [
        'page-transition',
        transitionStage || '',
        contentFadeOut ? 'content-fade-out' : '',
        contentDelay ? 'content-delayed' : '',
        fromLoading ? 'from-loading' : '',
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <>
          {showContent && <div className={classNames}>{children}</div>}
          <GlobalProgress
            status={statusForProgress}
            onBusyChange={onBusyChange}
            onTextVisible={onTextVisible}
          />
        </>
      );
    }

    return (
      <>
        {showContent && (
          <Router
            routes={routes}
            displayLocation={displayLocation}
            transitionStage={transitionStage}
            contentFadeOut={contentFadeOut}
            contentDelay={contentDelay}
            fromLoading={fromLoading}
          />
        )}
        <GlobalProgress
          status={statusForProgress}
          onBusyChange={onBusyChange}
          onTextVisible={onTextVisible}
        />
      </>
    );
  }

  // Se tem children, renderiza com animações de status E de rota
  if (children) {
    const classNames = [
      'page-transition',
      transitionStage || '',
      contentFadeOut ? 'content-fade-out' : '',
      contentDelay ? 'content-delayed' : '',
      fromLoading ? 'from-loading' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return <div className={classNames}>{children}</div>;
  }

  // Se tem routes, usa o Router
  return (
    <Router
      routes={routes}
      displayLocation={displayLocation}
      transitionStage={transitionStage}
      contentFadeOut={contentFadeOut}
      contentDelay={contentDelay}
      fromLoading={fromLoading}
    />
  );
};

ContentSwitcher.propTypes = {
  displayStatus: PropTypes.object,
  globalProgressBusy: PropTypes.bool.isRequired,
  statusForProgress: PropTypes.object,
  onBusyChange: PropTypes.func.isRequired,
  onTextVisible: PropTypes.func,
  routes: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node,
  displayLocation: PropTypes.object.isRequired,
  transitionStage: PropTypes.string.isRequired,
  contentFadeOut: PropTypes.bool.isRequired,
  contentDelay: PropTypes.bool.isRequired,
  fromLoading: PropTypes.bool.isRequired,
};

export default ContentSwitcher;
